import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import { Send, Square, SquarePen, Info, Menu, History, BookOpen } from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { ConversationSidebar } from '@/components/ConversationSidebar';
import { useConversation } from '@/lib/hooks/useConversation';
import { api, ConversationMessageInput } from '@/lib/api';
import { ArcheLayout } from '../../components/ArcheLayout';
import { useSearchResults } from '../../context/SearchResultsContext';
import { useSidebar } from '../../context/SidebarContext';
import { useFiasAI, FiasAIMessage } from './hooks/useFiasAI';
import { OptionsMenu } from './components/OptionsMenu';
import { MessageBubble } from './components/MessageBubble';
import { ArcheResultsBlock } from './components/ArcheResultsBlock';
import { ArcheSuggestionsRow } from './components/ArcheSuggestionsRow';
import { persistModeOptions, readInitialModeOptions } from './lib/modeOptionsStorage';

const ARCHE_ID = 'arche_make_a_request';

function MakeARequestContent() {
  const navigate = useNavigate();
  const [inputValue, setInputValue] = useState('');
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const { setSearchResults, clearSearchResults } = useSearchResults();
  const { isCollapsed, isMobile, toggle, setCollapsed } = useSidebar();

  // Fias AI conversation requires subscription or fias credits (paid features).
  const [aiOutOfCredits, setAiOutOfCredits] = useState(false);
  // Semantic search is a core system feature — system credits are eligible.
  const [searchOutOfCredits, setSearchOutOfCredits] = useState(false);

  // Read persisted options once on mount. Lazy initializer keeps the
  // restored values synchronous so the toggles don't flash defaults first.
  const initialModeOptions = useMemo(() => readInitialModeOptions(), []);
  const [rememberState, setRememberStateBase] = useState(initialModeOptions.rememberState);

  const {
    mode,
    setSearchEnabled,
    setAIEnabled,
    messages,
    setMessages,
    submit,
    stop,
    reset,
    isStreaming,
    isSearching,
    latestSearchResults,
    latestSearchQuery,
  } = useFiasAI({
    initialSearchEnabled: initialModeOptions.searchEnabled,
    initialAIEnabled: initialModeOptions.aiEnabled,
  });

  const handleSetSearchEnabled = useCallback(
    (v: boolean) => {
      setSearchEnabled(v);
      persistModeOptions({ rememberState, searchEnabled: v, aiEnabled: mode.aiEnabled });
    },
    [setSearchEnabled, rememberState, mode.aiEnabled],
  );

  const handleSetAIEnabled = useCallback(
    (v: boolean) => {
      setAIEnabled(v);
      persistModeOptions({ rememberState, searchEnabled: mode.searchEnabled, aiEnabled: v });
    },
    [setAIEnabled, rememberState, mode.searchEnabled],
  );

  const handleSetRememberState = useCallback(
    (v: boolean) => {
      setRememberStateBase(v);
      persistModeOptions({
        rememberState: v,
        searchEnabled: mode.searchEnabled,
        aiEnabled: mode.aiEnabled,
      });
    },
    [mode.searchEnabled, mode.aiEnabled],
  );

  // Refs let the polling effect read the latest mode/setter without retriggering
  // the effect (and restarting the polling timer) on every AI toggle.
  const aiEnabledRef = useRef(mode.aiEnabled);
  const setAIEnabledRef = useRef(setAIEnabled);
  useEffect(() => {
    aiEnabledRef.current = mode.aiEnabled;
    setAIEnabledRef.current = setAIEnabled;
  }, [mode.aiEnabled, setAIEnabled]);

  // Poll the credit balance every 60 seconds while the page is visible. The
  // visibility check pauses the timer when the user switches tabs (saves API
  // quota) and the visibilitychange listener triggers an immediate refresh
  // when they come back so the toggle reflects current state without delay.
  useEffect(() => {
    let cancelled = false;
    let interval: ReturnType<typeof setInterval> | null = null;

    const checkBalance = () => {
      api
        .getCreditBalance()
        .then((data) => {
          if (cancelled) return;
          const paidBalance = data.buckets.subscription + data.buckets.fias;
          const totalBalance = paidBalance + data.buckets.system;
          setAiOutOfCredits(paidBalance <= 0);
          setSearchOutOfCredits(totalBalance <= 0);
          // If we just discovered paid credits are gone, force AI off so
          // submissions don't fail at the server.
          if (paidBalance <= 0 && aiEnabledRef.current) {
            setAIEnabledRef.current(false);
          }
        })
        .catch(() => {});
    };

    const startPolling = () => {
      if (interval !== null) return;
      checkBalance();
      interval = setInterval(checkBalance, 60_000);
    };

    const stopPolling = () => {
      if (interval !== null) {
        clearInterval(interval);
        interval = null;
      }
    };

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        startPolling();
      } else {
        stopPolling();
      }
    };

    if (document.visibilityState === 'visible') {
      startPolling();
    }
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      cancelled = true;
      stopPolling();
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, []);

  // Conversation persistence
  const {
    conversation,
    conversations,
    loading: conversationsLoading,
    createConversation,
    loadConversation,
    deleteConversationById,
    clearConversation,
    refreshList,
  } = useConversation({
    archeId: ARCHE_ID,
  });

  const isBusy = isStreaming || isSearching;

  // Track pending save: stores user query when submit is called
  const pendingSaveRef = useRef<string | null>(null);
  // Track whether the conversation was just created to avoid re-restoring messages
  const justCreatedConversationRef = useRef(false);
  // Track last loaded conversation ID to avoid duplicate restores
  const lastLoadedConversationIdRef = useRef<string | null>(null);
  // Track previous busy state to detect completion
  const wasBusyRef = useRef(false);

  // Open sidebar by default on desktop
  useEffect(() => {
    if (!isMobile) {
      setCollapsed(false);
    }
  }, [isMobile, setCollapsed]);

  // Sync search results to sidebar context
  useEffect(() => {
    if (latestSearchResults && latestSearchResults.length > 0) {
      setSearchResults(
        latestSearchResults.map((r) => ({
          archId: r.archId,
          slug: null,
          name: r.name,
          simpleDescription: r.simpleDescription,
          imageRef: r.imageRef,
          categoryId: null,
          categoryName: null,
          categorySlug: null,
          tags: [],
          fitnessScore: r.fitnessScore,
          invocationCount: 0,
          pricing: { model: 'free' as const, currency: 'usd' as const },
          actionPricing: null,
          hasPerUseCredits: false,
          isStatic: false,
          archeType: 'tool' as const,
          renderMode: 'default' as const,
          relevance: r.relevance,
          contributorId: '',
          isPublic: true,
          isFullScreen: false,
        })),
        latestSearchQuery || '',
      );
    }
  }, [latestSearchResults, latestSearchQuery, setSearchResults]);

  // Restore messages from loaded conversation (when clicking history item)
  useEffect(() => {
    const currentConvId = conversation?.conversationId || null;

    // Skip if we just created this conversation
    if (justCreatedConversationRef.current) {
      justCreatedConversationRef.current = false;
      lastLoadedConversationIdRef.current = currentConvId;
      return;
    }

    // Only restore if this is a different conversation than last loaded
    if (currentConvId !== lastLoadedConversationIdRef.current) {
      lastLoadedConversationIdRef.current = currentConvId;

      if (conversation?.messages && conversation.messages.length > 0) {
        const loadedMessages: FiasAIMessage[] = conversation.messages.map((msg) => {
          const metadata = msg.metadata as Record<string, unknown> | null;
          return {
            id: msg.messageId,
            role: msg.role,
            content: msg.content,
            isStreaming: false,
            searchResults: metadata?.searchResults as FiasAIMessage['searchResults'],
            recommendedArches: metadata?.recommendedArches as FiasAIMessage['recommendedArches'],
            modelInfo: msg.provider
              ? {
                  provider: msg.provider,
                  modelName: msg.modelName || '',
                  displayName: msg.modelName || msg.provider,
                  tier: 'standard',
                }
              : undefined,
          };
        });
        setMessages(loadedMessages);
      } else if (!conversation) {
        setMessages([]);
      }
    }
  }, [conversation, setMessages]);

  // Save messages after streaming/search completes
  useEffect(() => {
    const wasBusy = wasBusyRef.current;
    wasBusyRef.current = isBusy;

    // Detect transition from busy → idle
    if (wasBusy && !isBusy && pendingSaveRef.current) {
      const userContent = pendingSaveRef.current;
      pendingSaveRef.current = null;

      const saveMessages = async () => {
        try {
          // Find the assistant message that was just completed
          const assistantMsg = [...messages]
            .reverse()
            .find((m) => m.role === 'assistant' && !m.isStreaming);
          if (!assistantMsg) return;

          // Build metadata for the assistant message
          const metadata: Record<string, unknown> = {};
          if (assistantMsg.searchResults && assistantMsg.searchResults.length > 0) {
            metadata.searchResults = assistantMsg.searchResults;
          }
          if (assistantMsg.recommendedArches && assistantMsg.recommendedArches.length > 0) {
            metadata.recommendedArches = assistantMsg.recommendedArches;
          }

          // Create or use existing conversation
          let convId = conversation?.conversationId;
          let isNewConversation = false;

          if (!convId) {
            justCreatedConversationRef.current = true;
            const title = userContent.slice(0, 100) + (userContent.length > 100 ? '...' : '');
            const newConv = await createConversation(title);
            convId = newConv?.conversationId;
            isNewConversation = true;
          }

          if (!convId) return;

          const messagesToSave: ConversationMessageInput[] = [
            { role: 'user', content: userContent },
            {
              role: 'assistant',
              content: assistantMsg.content,
              provider: assistantMsg.modelInfo?.provider as
                | 'openai'
                | 'anthropic'
                | 'google'
                | undefined,
              modelName: assistantMsg.modelInfo?.modelName,
              metadata: Object.keys(metadata).length > 0 ? metadata : undefined,
            },
          ];

          // Use API directly for new conversations to avoid stale closure
          if (isNewConversation) {
            await api.addMessages(convId, messagesToSave);
          } else {
            await api.addMessages(convId, messagesToSave);
            // Move conversation to top of list
            await refreshList();
          }
        } catch (err) {
          console.error('Failed to save messages:', err);
        }
      };
      saveMessages();
    }
  }, [isBusy, messages, conversation, createConversation, refreshList]);

  // Reset conversation
  const handleReset = useCallback(() => {
    reset();
    setInputValue('');
    clearSearchResults();
    pendingSaveRef.current = null;
    lastLoadedConversationIdRef.current = null;
    clearConversation();
    if (!isMobile) {
      textareaRef.current?.focus();
    }
  }, [reset, clearSearchResults, isMobile, clearConversation]);

  // Listen for global history clear event (from Account Settings)
  useEffect(() => {
    const handleHistoryCleared = () => {
      setMessages([]);
      setInputValue('');
      pendingSaveRef.current = null;
      lastLoadedConversationIdRef.current = null;
      reset();
      clearConversation();
      clearSearchResults();
      refreshList();
    };

    window.addEventListener('fias:history-cleared', handleHistoryCleared);
    return () => {
      window.removeEventListener('fias:history-cleared', handleHistoryCleared);
    };
  }, [reset, clearConversation, refreshList, clearSearchResults, setMessages]);

  // Auto-scroll to bottom when messages change
  const scrollToBottom = useCallback(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, []);

  useEffect(() => {
    scrollToBottom();
  }, [messages, scrollToBottom]);

  // Focus textarea when not busy (skip on mobile)
  useEffect(() => {
    if (!isBusy && !isMobile) {
      textareaRef.current?.focus();
    }
  }, [isBusy, isMobile]);

  // Auto-resize textarea based on content
  const adjustTextareaHeight = useCallback(() => {
    const textarea = textareaRef.current;
    if (textarea) {
      textarea.style.height = 'auto';
      textarea.style.height = `${Math.min(textarea.scrollHeight, 200)}px`;
    }
  }, []);

  useEffect(() => {
    adjustTextareaHeight();
  }, [inputValue, adjustTextareaHeight]);

  const handleSubmit = async (e?: React.FormEvent) => {
    e?.preventDefault();
    const trimmedInput = inputValue.trim();
    if (!trimmedInput || isBusy) return;

    pendingSaveRef.current = trimmedInput;
    setInputValue('');
    await submit(trimmedInput, { aiOutOfCredits, searchOutOfCredits });
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSubmit();
    }
  };

  // Conversation sidebar handlers
  const handleLoadConversation = useCallback(
    async (conversationId: string) => {
      await loadConversation(conversationId);
      clearSearchResults();
    },
    [loadConversation, clearSearchResults],
  );

  const handleUpdateTitle = useCallback(
    async (conversationId: string, title: string) => {
      if (conversation?.conversationId === conversationId) {
        await api.updateConversation(conversationId, { title });
      } else {
        await api.updateConversation(conversationId, { title });
      }
      await refreshList();
    },
    [conversation, refreshList],
  );

  const handleArchiveConversation = useCallback(
    async (conversationId: string) => {
      if (conversation?.conversationId === conversationId) {
        setMessages([]);
        lastLoadedConversationIdRef.current = null;
        clearConversation();
      }
      await api.updateConversation(conversationId, { status: 'archived' });
      await refreshList();
    },
    [conversation, clearConversation, refreshList, setMessages],
  );

  const handleDeleteConversation = useCallback(
    async (conversationId: string) => {
      const isActiveConversation = conversation?.conversationId === conversationId;
      await deleteConversationById(conversationId);
      if (isActiveConversation) {
        setMessages([]);
        lastLoadedConversationIdRef.current = null;
        pendingSaveRef.current = null;
        justCreatedConversationRef.current = false;
      }
    },
    [conversation, deleteConversationById, setMessages],
  );

  const handleExportConversation = useCallback(
    async (conversationId: string, format: 'json' | 'markdown') => {
      try {
        const content = await api.exportConversation(conversationId, format);
        if (content) {
          const blob = new Blob([content], {
            type: format === 'json' ? 'application/json' : 'text/markdown',
          });
          const url = URL.createObjectURL(blob);
          const a = document.createElement('a');
          a.href = url;
          a.download = `conversation-${conversationId}.${format === 'json' ? 'json' : 'md'}`;
          document.body.appendChild(a);
          a.click();
          document.body.removeChild(a);
          URL.revokeObjectURL(url);
        }
      } catch (err) {
        console.error('Failed to export conversation:', err);
      }
    },
    [],
  );

  const handleShareConversation = useCallback(async (conversationId: string) => {
    try {
      const result = await api.shareConversation(conversationId);
      const fullUrl = `${window.location.origin}/shared/${result.shareId}`;
      await navigator.clipboard.writeText(fullUrl);
    } catch (err) {
      console.error('Failed to share conversation:', err);
    }
  }, []);

  return (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div className="flex-shrink-0 border-b border-border bg-card/50 backdrop-blur-sm">
        <div className="px-4 py-3 flex items-center gap-4">
          {/* Left: sidebar toggle + title */}
          <div className="flex items-center gap-2 flex-1 min-w-0">
            <Button
              variant="outline"
              size="icon"
              onClick={toggle}
              className={`h-11 w-11 flex-shrink-0 bg-gray-100 dark:bg-zinc-700 ${isCollapsed ? '' : 'invisible'}`}
              title="Open sidebar"
            >
              <Menu className="h-6 w-6" />
            </Button>
            <h1 className="text-lg font-semibold leading-tight">Make a Re-Quest</h1>
            <Link
              to="/marketplace/arches/arche_make_a_request"
              className="text-muted-foreground hover:text-primary transition-colors flex-shrink-0"
              title="View arche details"
            >
              <Info className="h-5 w-5" />
            </Link>
          </div>

          {/* Right: actions */}
          <div className="flex items-center gap-3 flex-1 justify-end">
            <Button variant="outline" size="sm" onClick={() => navigate('/a/arche_favorites')}>
              Worktop
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={handleReset}
              disabled={isBusy || messages.length === 0}
              className="gap-1.5 [&_svg]:size-5!"
            >
              <SquarePen />
              New
            </Button>
          </div>
        </div>
      </div>

      {messages.length === 0 ? (
        <>
          {/* Empty state: centered content with input */}
          <div className="flex-1 flex flex-col">
            {/* Beta ReadMe entry point — pinned just under the header */}
            <div className="flex-shrink-0 flex justify-center pt-3 pb-1">
              <BetaReadmeButton />
            </div>
            {/* Centered logo, title, and input */}
            <div className="flex-1 flex flex-col justify-center">
              <div className="text-center mb-6">
                <img
                  src="/fias-logo-image.png"
                  alt="Fias AI"
                  className="h-16 w-16 mb-4 mx-auto dark:invert dark:brightness-90"
                />
                <h2 className="text-xl font-medium text-foreground mb-2">Make a Re-Quest</h2>
                <p className="text-muted-foreground">Ask Fias AI Anything and Search for Arches.</p>
              </div>
              <div className="max-w-3xl mx-auto px-4 w-full">
                <form onSubmit={handleSubmit} className="relative">
                  <Textarea
                    ref={textareaRef}
                    value={inputValue}
                    onChange={(e) => setInputValue(e.target.value)}
                    onKeyDown={handleKeyDown}
                    placeholder={
                      mode.aiEnabled ? 'Ask Fias AI anything...' : 'What is your Re-Quest?'
                    }
                    disabled={isBusy}
                    className="min-h-[100px] max-h-[200px] pr-14 resize-none rounded-2xl bg-muted/50 overflow-y-auto text-base"
                    rows={3}
                  />
                  <Button
                    type="submit"
                    size="icon"
                    disabled={!inputValue.trim() || isBusy}
                    className="absolute right-2 bottom-3 h-10 w-10 rounded-xl"
                  >
                    <Send className="h-4 w-4" />
                  </Button>
                </form>
                <p className="text-xs text-muted-foreground text-center mt-2">
                  Press Enter to send, Shift+Enter for new line
                </p>
                <div className="flex justify-center items-center gap-2 mt-4">
                  <OptionsMenu
                    searchEnabled={mode.searchEnabled}
                    aiEnabled={mode.aiEnabled}
                    rememberState={rememberState}
                    onSearchToggle={handleSetSearchEnabled}
                    onAIToggle={handleSetAIEnabled}
                    onRememberToggle={handleSetRememberState}
                    disabled={isBusy}
                    aiOutOfCredits={aiOutOfCredits}
                  />
                  {conversations.length > 0 && (
                    <ConversationSidebar
                      conversations={conversations}
                      activeConversationId={conversation?.conversationId || null}
                      loading={conversationsLoading}
                      onNewConversation={handleReset}
                      onLoadConversation={handleLoadConversation}
                      onUpdateTitle={handleUpdateTitle}
                      onArchiveConversation={handleArchiveConversation}
                      onDeleteConversation={handleDeleteConversation}
                      onExportConversation={handleExportConversation}
                      onShareConversation={handleShareConversation}
                      isStreaming={isStreaming}
                      trigger={
                        <Button variant="outline" size="default" className="gap-2">
                          <History className="h-4 w-4" />
                          History
                        </Button>
                      }
                    />
                  )}
                </div>
              </div>
            </div>

            {/* Bottom-pinned social icons and legal links */}
            <div className="flex-shrink-0 pb-4">
              <div className="flex items-center justify-center gap-5 mt-3">
                <a
                  href="https://x.com/tomlucient"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-muted-foreground hover:text-foreground transition-colors"
                  title="X"
                >
                  <svg className="h-6 w-6" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
                  </svg>
                </a>
                <a
                  href="https://linkedin.com/company/fiasai"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-muted-foreground hover:text-foreground transition-colors"
                  title="LinkedIn"
                >
                  <svg className="h-6 w-6" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 01-2.063-2.065 2.064 2.064 0 112.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" />
                  </svg>
                </a>
                <a
                  href="https://fias.io"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-muted-foreground hover:text-foreground transition-colors"
                  title="Fias.io"
                >
                  <img src="/fias-logo-image.png" alt="Fias" className="h-6 w-6 dark:invert" />
                </a>
                <a
                  href="https://t.me/FiasAI"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-muted-foreground hover:text-foreground transition-colors"
                  title="Telegram"
                >
                  <svg className="h-6 w-6" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M11.944 0A12 12 0 000 12a12 12 0 0012 12 12 12 0 0012-12A12 12 0 0012 0a12 12 0 00-.056 0zm4.962 7.224c.1-.002.321.023.465.14a.506.506 0 01.171.325c.016.093.036.306.02.472-.18 1.898-.962 6.502-1.36 8.627-.168.9-.499 1.201-.82 1.23-.696.065-1.225-.46-1.9-.902-1.056-.693-1.653-1.124-2.678-1.8-1.185-.78-.417-1.21.258-1.91.177-.184 3.247-2.977 3.307-3.23.007-.032.014-.15-.056-.212s-.174-.041-.249-.024c-.106.024-1.793 1.14-5.061 3.345-.479.33-.913.49-1.302.48-.428-.008-1.252-.241-1.865-.44-.752-.245-1.349-.374-1.297-.789.027-.216.325-.437.893-.663 3.498-1.524 5.83-2.529 6.998-3.014 3.332-1.386 4.025-1.627 4.476-1.635z" />
                  </svg>
                </a>
                <a
                  href="https://www.youtube.com/playlist?list=PLQdfOryRxAcel4oLrZX6Y_89JV4q030mZ"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-muted-foreground hover:text-foreground transition-colors"
                  title="YouTube"
                >
                  <svg className="h-6 w-6" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M23.498 6.186a3.016 3.016 0 00-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 00.502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 002.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 002.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z" />
                  </svg>
                </a>
                <a
                  href="https://medium.com/@tomlucient"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-muted-foreground hover:text-foreground transition-colors"
                  title="Medium"
                >
                  <svg className="h-6 w-6" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M13.54 12a6.8 6.8 0 01-6.77 6.82A6.8 6.8 0 010 12a6.8 6.8 0 016.77-6.82A6.8 6.8 0 0113.54 12zm7.42 0c0 3.54-1.51 6.42-3.38 6.42-1.87 0-3.39-2.88-3.39-6.42s1.52-6.42 3.39-6.42 3.38 2.88 3.38 6.42M24 12c0 3.17-.53 5.75-1.19 5.75-.66 0-1.19-2.58-1.19-5.75s.53-5.75 1.19-5.75C23.47 6.25 24 8.83 24 12z" />
                  </svg>
                </a>
              </div>
              <div className="text-center text-xs text-muted-foreground mt-2 space-x-3">
                <Link to="/legal/terms" className="hover:text-foreground transition-colors">
                  Terms of Service
                </Link>
                <span>·</span>
                <Link to="/legal/privacy" className="hover:text-foreground transition-colors">
                  Privacy Policy
                </Link>
                <span>·</span>
                <Link
                  to="/legal/acceptable-use"
                  className="hover:text-foreground transition-colors"
                >
                  Acceptable Use Policy
                </Link>
              </div>
            </div>
          </div>
        </>
      ) : (
        <>
          {/* Conversation: messages area */}
          <div className="flex-1 overflow-y-auto">
            <div className="max-w-3xl mx-auto px-4 py-6">
              <div className="space-y-6">
                {messages.map((message) => (
                  <div key={message.id}>
                    <MessageBubble
                      role={message.role}
                      content={message.content}
                      isStreaming={message.isStreaming}
                      modelInfo={message.modelInfo}
                    />
                    {/* AI mode: compact suggestion row after completed messages */}
                    {mode.aiEnabled &&
                      message.role === 'assistant' &&
                      !message.isStreaming &&
                      (() => {
                        const suggestions = message.recommendedArches ?? message.searchResults;
                        return suggestions && suggestions.length > 0 ? (
                          <ArcheSuggestionsRow
                            suggestions={suggestions.slice(0, 3)}
                            onArcheClick={(archId) => navigate(`/a/${archId}`)}
                          />
                        ) : null;
                      })()}
                    {/* In search-only mode, show Arche results in the main panel */}
                    {!mode.aiEnabled && message.role === 'assistant' && message.searchResults && (
                      <ArcheResultsBlock
                        results={message.searchResults.slice(0, 3)}
                        totalResults={message.searchResults.length}
                        onArcheClick={(archId) => navigate(`/a/${archId}`)}
                      />
                    )}
                  </div>
                ))}
                <div ref={messagesEndRef} />
              </div>
            </div>
          </div>

          {/* Bottom input bar */}
          <div className="flex-shrink-0 border-t border-border bg-background">
            <div className="max-w-3xl mx-auto px-4 py-4">
              <form onSubmit={handleSubmit} className="relative">
                <Textarea
                  ref={textareaRef}
                  value={inputValue}
                  onChange={(e) => setInputValue(e.target.value)}
                  onKeyDown={handleKeyDown}
                  placeholder={
                    mode.aiEnabled ? 'Ask Fias AI anything...' : 'What is your Re-Quest?'
                  }
                  disabled={isBusy}
                  className="min-h-[44px] max-h-[200px] pr-14 resize-none rounded-2xl bg-muted/50 overflow-y-auto"
                  rows={1}
                  style={{ height: 'auto' }}
                />
                {isStreaming ? (
                  <Button
                    type="button"
                    size="icon"
                    variant="destructive"
                    onClick={stop}
                    className="absolute right-2 top-1/2 -translate-y-1/2 h-10 w-10 rounded-xl"
                  >
                    <Square className="h-4 w-4" />
                  </Button>
                ) : (
                  <Button
                    type="submit"
                    size="icon"
                    disabled={!inputValue.trim() || isBusy}
                    className="absolute right-2 top-1/2 -translate-y-1/2 h-10 w-10 rounded-xl"
                  >
                    <Send className="h-4 w-4" />
                  </Button>
                )}
              </form>
              <p className="text-xs text-muted-foreground text-center mt-2">
                Press Enter to send, Shift+Enter for new line
              </p>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

function BetaReadmeButton() {
  return (
    <Dialog>
      <DialogTrigger asChild>
        <Button
          variant="outline"
          size="sm"
          className="gap-1.5 border-primary/40 bg-primary/5 text-primary hover:bg-primary/10 hover:text-primary"
        >
          <BookOpen className="h-4 w-4" />
          Beta ReadMe
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Welcome to the Fias Closed Beta</DialogTitle>
        </DialogHeader>
        <ul className="list-disc space-y-3 pl-5 text-sm leading-relaxed text-foreground">
          <li>
            Fias is in a Closed-Beta state. A limited number of people (including you) have been
            allowed access to the system. Because it is Beta, there are still bugs and issues. You
            can report bugs and make platform suggestions on our{' '}
            <Link
              to="/a/arche_customer_support"
              className="text-primary underline hover:opacity-80"
            >
              support page
            </Link>
            .
          </li>
          <li>
            We appreciate your help working out problems in the system and helping us to prepare for
            a public launch! Potential investors will watch our early growth, so please help us in
            creating some great initial momentum.
          </li>
          <li>
            Check out the Worktop (which you can access in the top header). This shows your personal
            Arches (i.e. your apps) that you most use. Also check out the Arche Store and see what
            other Arches are available (from us, and from your fellow builders). In any Arche&apos;s
            info panel or in its header, you can click the Star icon to add/remove Arches from your
            Worktop.
          </li>
          <li>
            If you want to build your own Fias web apps, which we call Arches, you can do so in two
            ways. You can implement Arches in our{' '}
            <Link to="/a/arche_plugin_builder" className="text-primary underline hover:opacity-80">
              Arche Builder
            </Link>{' '}
            within the Fias platform, or you can implement Arches with our plugin approach using
            your own subscription to Claude Code, Codex, etc.
            <ul className="mt-2 list-[circle] space-y-2 pl-5">
              <li>
                Instructions for the plugin approach are given in the Arche Builder as an option
                when you create a new Arche.
              </li>
              <li>
                You can build Arches for your own use, or to sell and make money. You can add in-app
                purchases, subscriptions, etc to your Arches for your own monetization.
              </li>
              <li>More complex Arches are naturally suited to the plugin approach.</li>
              <li>
                If you are using the Arche Builder and it gets stuck, you can click Report, and we
                can help fix problems rather than wasting credits in trying to fix something that
                Fias AI is struggling to fix.
              </li>
              <li>
                Ask questions in our{' '}
                <Link
                  to="/a/arche_chat/channel/cch_global_fias_community"
                  className="text-primary underline hover:opacity-80"
                >
                  community chat
                </Link>
                .
              </li>
              <li>
                If your Arche needs access to external systems, you can request the addition (which
                we implement through &quot;Entities&quot;) in the support page.
              </li>
              <li>
                There are known limitations for creating Arches right now, but we expect in a short
                amount of time the possibilities will be nearly endless. We will soon add concepts
                like web search, automations, new AI models, MCP comms, etc. Let us know what
                features are most important to you.
              </li>
            </ul>
          </li>
        </ul>
        <p className="text-sm text-muted-foreground">
          Our builder community is critical to our growth and success, so anything you need to make
          your experience a good one, let us know!
        </p>
      </DialogContent>
    </Dialog>
  );
}

export function MakeARequestPage() {
  return (
    <ArcheLayout>
      <MakeARequestContent />
    </ArcheLayout>
  );
}

export default MakeARequestPage;
